import { color, sizeColors } from "./color";

/**
 * Semantische Farbdefinitionen, die funktionale Rollen zu tatsächlichen Farben zuordnen.
 * Diese definieren, welche Farben für bestimmte UI-Zwecke verwendet werden sollen.
 *
 * Diese Trennung ermöglicht es uns:
 * 1. Das visuelle Erscheinungsbild durch Änderung von color.ts zu ändern
 * 2. Semantische Zuordnungen für verschiedene Themes (hell/dunkel) zu ändern
 * 3. Konsistenz zwischen Komponenten zu gewährleisten
 */

export interface SemanticColors {
    // Textfarben
    text: {
        heading: string; // Überschrifttext (schwarz)
        paragraph: string; // Haupttext (schwarz)
        dimmed: string; // Dimmed Text (schwarz 80%)
        inverted: string; // Invertierte Textfarbe (weiß)
    };

    chat: {
        bot: {
            primary: string;
            secondary: string;
            text: string;
        };
        user: {
            primary: string;
            secondary: string;
            text: string;
        };
    };

    logo: {
        primary: string;
    };

    highlight: {
        primary: string;
        secondary: string;
    };

    // Icon-Farben
    icon: {
        default: string; // Default Icons (schwarz)
        informative: string; // Icons (schwarz) - informativ
        functional: string; // Funktionale Icons (getrennt von informative)
        send: string; // Separater Sendebutton
    };

    // Button-Farben
    button: {
        gradient: string; // Gradient Button
        gradientHover: string; // Gradient Button Hover
        primary: string; // Standard Button → Gradient
        primaryHover: string; // Button Hover-Zustand
        text: string; // Text Button → Textfarbe
        disabled: string; // Disabled Button → Grau
        outline: string; // Invertierter Button → Gradient als outline
        outlineHover: string; // Outline Button Hover
    };

    // Karten/Oberflächen-Farben
    surface: {
        primary: string; // Karten primary: grau
        secondary: string; // Karten secondary: grau mit 50%
        input: string; // Input-Felder: grau
    };

    // Navigations-Farben
    navigation: {
        button: string; // Navigationsbuttons für Settings Rand und Header
        buttonHover: string; // Navigation Button Hover
        border: string; // Navigation Ränder
    };

    // Benachrichtigungsfarben
    notification: {
        error: string;
        success: string;
        warning: string;
        info: string;
    };

    // Größenspezifische Farben
    planSize: {
        S: string;
        M: string;
        L: string;
    };

    // Spezielle Zweck-Farben
    special: {
        linkedin: string; // LinkedIn Logo von Farbe trennen
    };
}

// Helle Theme semantische Farben
export const lightSemanticColors: SemanticColors = {
    text: {
        heading: color.black.main,
        paragraph: color.black.main,
        dimmed: `${color.black.main}99`,
        inverted: color.white.main,
    },

    chat: {
        bot: {
            primary: color.gray.main,
            secondary: color.gray.light,
            text: color.black.main,
        },
        user: {
            primary: color.brand.one,
            secondary: `${color.brand.one}90`,
            text: color.white.main,
        },
    },

    logo: {
        primary: color.brand.one,
    },

    highlight: {
        primary: color.brand.two,
        secondary: color.brand.two,
    },

    icon: {
        default: color.black.main,
        informative: color.black.main,
        functional: color.black.main,
        send: color.green.dark, // Sendebutton vorerst mit Grün beibehalten
    },

    button: {
        gradient: `linear-gradient(135deg, ${color.brand.one}, ${color.brand.two})`,
        gradientHover: `linear-gradient(135deg, ${color.brand.two}, ${color.brand.one})`,
        primary: color.brand.one,
        primaryHover: color.brand.one,
        text: color.white.main,
        disabled: color.gray.main,
        outline: color.brand.text,
        outlineHover: color.brand.one,
    },

    surface: {
        primary: color.gray.main,
        secondary: color.gray.light,
        input: color.brand.background,
    },

    navigation: {
        button: color.brand.one,
        buttonHover: color.brand.one,
        border: color.brand.one,
    },

    notification: {
        error: color.red.dark,
        success: color.brand.one,
        warning: color.brand.two,
        info: color.brand.one,
    },

    planSize: {
        // S: `${color.brand.one}50`,
        // M: `${color.brand.one}85`,
        // L: `${color.brand.one}`,
        S: sizeColors.S,
        M: sizeColors.M,
        L: sizeColors.L,
    },

    special: {
        linkedin: color.blue.main,
    },
};

// Dunkle Theme semantische Farben (vorbereitet für die Zukunft)
export const darkSemanticColors: SemanticColors = {
    text: {
        heading: color.white.main,
        paragraph: color.white.main,
        dimmed: color.white.main,
        inverted: color.black.main,
    },

    chat: {
        bot: {
            primary: color.green.light,
            secondary: color.green.light,
            text: color.green.dark,
        },
        user: {
            primary: color.gray.main,
            secondary: color.gray.light,
            text: color.black.main,
        },
    },
    logo: {
        primary: color.green.light,
    },

    highlight: {
        primary: color.brown.light,
        secondary: color.brown.dark,
    },

    icon: {
        default: color.white.main,
        informative: color.white.main,
        functional: color.brand.two,
        send: color.green.light,
    },

    button: {
        gradient: `linear-gradient(180deg, ${color.brand.one}, ${color.brand.two})`,
        gradientHover: `linear-gradient(180deg, ${color.brand.one}, ${color.brand.two})`,
        primary: color.green.light,
        primaryHover: color.green.light,
        text: color.black.main,
        disabled: color.gray.main,
        outline: color.white.main,
        outlineHover: color.green.light,
    },

    surface: {
        primary: "#2a2a2a",
        secondary: "#2a2a2a80",
        input: color.brand.background,
    },

    navigation: {
        button: color.green.light,
        buttonHover: color.green.light,
        border: color.green.light,
    },

    notification: {
        error: color.red.dark,
        success: color.green.dark,
        warning: color.orange.dark,
        info: color.brand.two,
    },

    planSize: {
        S: sizeColors.S,
        M: sizeColors.M,
        L: sizeColors.L,
    },

    special: {
        linkedin: color.blue.main,
    },
};

export const semanticColors = lightSemanticColors;
